import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Sparkles, ChevronDown, ChevronUp, Trash2, Clock, MapPin,
  GripVertical, Copy, Eye, EyeOff, Edit2, Check, X, ExternalLink, Loader2, Lock, Unlock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_COLORS = {
  morning: { bg: 'bg-[#C5A059]/10', text: 'text-[#C5A059]', border: 'border-[#C5A059]/20' },
  afternoon: { bg: 'bg-[#8DA399]/10', text: 'text-[#8DA399]', border: 'border-[#8DA399]/20' },
  evening: { bg: 'bg-[#B37E6B]/10', text: 'text-[#B37E6B]', border: 'border-[#B37E6B]/20' },
};

const SOPHISTICATED_EMOJIS = ['🍽️', '🖼️', '🏖️', '🍸', '🛍️', '🧖', '🎭', '🌅', '🏛️', '🚢', '✨', '🌿'];

// Helper: Parse date as local to avoid timezone issues
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const parts = dateStr.split('T')[0].split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date(dateStr);
}

// Helper: Calculate date for a day number based on trip start date
function getDayDate(startDate, dayNumber) {
  if (!startDate) return null;
  const start = parseLocalDate(startDate);
  if (!start) return null;
  start.setDate(start.getDate() + (dayNumber - 1));
  return start;
}

function formatDayLabel(startDate, dayNumber, totalDays, endDate) {
  const date = getDayDate(startDate, dayNumber);
  if (!date || isNaN(date.getTime())) {
    return `Day ${dayNumber} — Date TBD`;
  }
  
  // Check if this day exceeds the trip end date
  const end = endDate ? parseLocalDate(endDate) : null;
  if (end && date > end) {
    return `Day ${dayNumber} — Date TBD`;
  }
  
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `Day ${dayNumber} — ${dayOfWeek}, ${monthDay}`;
}

// Activity Detail Modal (Read-only view)
function ActivityDetailModal({ activity, isOpen, onClose }) {
  if (!isOpen || !activity) return null;
  
  const colors = SECTION_COLORS[activity.section] || SECTION_COLORS.morning;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className={`text-xs px-2 py-1 rounded-full uppercase tracking-wide ${colors.bg} ${colors.text}`}>
                {activity.section}
              </span>
              <h3 className="font-serif text-xl text-[#1A1A1A] mt-2">{activity.name || 'Untitled'}</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-[#8C8C8C]" />
            </button>
          </div>
          
          {activity.time && (
            <div className="flex items-center gap-2 text-sm text-[#595959] mb-3">
              <Clock className="w-4 h-4" />
              {activity.time}
            </div>
          )}
          
          {activity.location_name && (
            <div className="flex items-center gap-2 text-sm text-[#595959] mb-3">
              <MapPin className="w-4 h-4" />
              {activity.location_name}
            </div>
          )}
          
          {activity.address && (
            <p className="text-sm text-[#8C8C8C] mb-3 pl-6">{activity.address}</p>
          )}
          
          {activity.description && (
            <div className="mb-4">
              <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-[#595959] whitespace-pre-wrap">{activity.description}</p>
            </div>
          )}
          
          {activity.website_url && (
            <a 
              href={activity.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#C5A059] hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Visit Website
            </a>
          )}
          
          {activity.photo_url && (
            <img 
              src={activity.photo_url} 
              alt={activity.name}
              className="w-full h-48 object-cover rounded-lg mt-4"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          
          <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-between">
            <span className={`text-xs ${activity.visible_to_client ? 'text-[#8DA399]' : 'text-[#B37E6B]'}`}>
              {activity.visible_to_client ? 'Visible to client' : 'Hidden from client'}
            </span>
            {activity.confirmation_id && (
              <span className="text-xs text-[#8C8C8C]">
                Confirmation: <code className="bg-[#F5F5F5] px-1 rounded font-mono">{activity.confirmation_id}</code>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Activity Card
function SortableActivityCard({ activity, onEdit, onDelete, onDuplicate, onToggleVisibility }) {
  const [editing, setEditing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [form, setForm] = useState(activity);
  const [saving, setSaving] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.id, data: { type: 'activity', activity } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/travels/activities/${activity.id}`, form);
      onEdit(activity.id, form);
      setEditing(false);
      toast.success('Activity saved');
    } catch (err) {
      toast.error('Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  const colors = SECTION_COLORS[activity.section] || SECTION_COLORS.morning;

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="bg-white rounded-lg border border-[rgba(0,0,0,0.08)] p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Activity name"
            className="bg-[#FAFAFA]"
          />
          <Input
            value={form.time || ''}
            onChange={(e) => setForm({ ...form, time: e.target.value })}
            placeholder="Time (e.g., 9:00 AM)"
            className="bg-[#FAFAFA]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={form.location_name || ''}
            onChange={(e) => setForm({ ...form, location_name: e.target.value })}
            placeholder="Location name"
            className="bg-[#FAFAFA]"
          />
          <Input
            value={form.address || ''}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Address"
            className="bg-[#FAFAFA]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={form.website_url || ''}
            onChange={(e) => setForm({ ...form, website_url: e.target.value })}
            placeholder="Website URL"
            className="bg-[#FAFAFA]"
          />
          <Input
            value={form.photo_url || ''}
            onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
            placeholder="Photo URL"
            className="bg-[#FAFAFA]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={form.confirmation_id || ''}
            onChange={(e) => setForm({ ...form, confirmation_id: e.target.value })}
            placeholder="Confirmation ID (optional)"
            className="bg-[#FAFAFA] font-mono text-sm"
          />
        </div>
        <Textarea
          value={form.description || ''}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description"
          className="bg-[#FAFAFA] min-h-[60px]"
        />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#2C2C2C]">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`group bg-white rounded-lg border border-[rgba(0,0,0,0.08)] p-3 hover:shadow-sm transition-shadow cursor-pointer ${
          !activity.visible_to_client ? 'opacity-60' : ''
        }`}
        onClick={() => setShowDetail(true)}
      >
        <div className="flex items-start gap-3">
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 p-1 cursor-grab active:cursor-grabbing text-[#8C8C8C] hover:text-[#1A1A1A]"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-[#1A1A1A] truncate">{activity.name || 'Untitled'}</span>
              {activity.time && (
                <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {activity.time}
                </span>
              )}
            </div>
            {activity.location_name && (
              <p className="text-sm text-[#8C8C8C] truncate">
                <MapPin className="w-3 h-3 inline mr-1" />
                {activity.location_name}
              </p>
            )}
            {activity.description && (
              <p className="text-sm text-[#595959] mt-1 line-clamp-2">{activity.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onToggleVisibility(activity)}
              className="p-1.5 rounded hover:bg-gray-100"
              title={activity.visible_to_client ? 'Hide from client' : 'Show to client'}
            >
              {activity.visible_to_client ? <Eye className="w-4 h-4 text-[#8C8C8C]" /> : <EyeOff className="w-4 h-4 text-[#B37E6B]" />}
            </button>
            <button onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-gray-100" title="Edit">
              <Edit2 className="w-4 h-4 text-[#8C8C8C]" />
            </button>
            <button onClick={() => onDuplicate(activity)} className="p-1.5 rounded hover:bg-gray-100" title="Duplicate">
              <Copy className="w-4 h-4 text-[#8C8C8C]" />
            </button>
            <button onClick={() => onDelete(activity.id)} className="p-1.5 rounded hover:bg-gray-100" title="Delete">
              <Trash2 className="w-4 h-4 text-[#B37E6B]" />
            </button>
          </div>
        </div>
      </div>
      
      <ActivityDetailModal 
        activity={activity} 
        isOpen={showDetail} 
        onClose={() => setShowDetail(false)} 
      />
    </>
  );
}

// Activity Card Overlay (for drag preview)
function ActivityCardOverlay({ activity }) {
  const colors = SECTION_COLORS[activity.section] || SECTION_COLORS.morning;
  return (
    <div className="bg-white rounded-lg border-2 border-[#C5A059] shadow-lg p-3 w-80">
      <div className="flex items-center gap-2">
        <span className="font-medium text-[#1A1A1A]">{activity.name || 'Untitled'}</span>
        {activity.time && (
          <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
            {activity.time}
          </span>
        )}
      </div>
    </div>
  );
}

// Section Component (Morning/Afternoon/Evening)
function DaySection({ dayId, section, activities, onAddActivity, onEdit, onDelete, onDuplicate, onToggleVisibility }) {
  const colors = SECTION_COLORS[section];
  const sectionActivities = activities.filter(a => a.section === section).sort((a, b) => a.order - b.order);
  
  return (
    <div className={`rounded-lg p-4 ${colors.bg} ${colors.border} border`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`text-sm font-medium ${colors.text} capitalize`}>{section}</h4>
        <button
          onClick={() => onAddActivity(dayId, section)}
          className={`p-1 rounded-full hover:bg-white/50 ${colors.text}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      
      <SortableContext items={sectionActivities.map(a => a.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {sectionActivities.map((activity) => (
            <SortableActivityCard
              key={activity.id}
              activity={activity}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onToggleVisibility={onToggleVisibility}
            />
          ))}
          {sectionActivities.length === 0 && (
            <p className="text-sm text-[#8C8C8C] text-center py-4 italic">No activities</p>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

// Sortable Day Card
function SortableDayCard({ day, startDate, endDate, activities, expanded, onToggle, onDelete, onAddActivity, onEditActivity, onDeleteActivity, onDuplicateActivity, onToggleActivityVisibility, onToggleDayVisibility, onToggleDayLock, onUpdateTeaser }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: day.id, data: { type: 'day', day } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dayLabel = formatDayLabel(startDate, day.day_number, 10, endDate);
  const dayActivities = activities.filter(a => a.day_id === day.id);
  const isVisible = day.visible_to_client !== false;
  const isLocked = day.locked === true;

  const [editingTeaser, setEditingTeaser] = useState(false);
  const [teaserText, setTeaserText] = useState(day.teaser_text || '');
  const [teaserRiddle, setTeaserRiddle] = useState(day.teaser_riddle || '');

  const handleSaveTeaser = async () => {
    try {
      await onUpdateTeaser(day.id, teaserText, teaserRiddle);
      setEditingTeaser(false);
      toast.success('Teaser updated');
    } catch (e) {
      toast.error('Failed to update teaser');
    }
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      layout
      className={`bg-white rounded-xl border border-[rgba(0,0,0,0.06)] overflow-hidden ${!isVisible ? 'opacity-60' : ''} ${isLocked ? 'border-l-4 border-l-[#C5A059]' : ''}`}
    >
      <div className="flex items-center p-4">
        <div
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab mr-2 text-[#8C8C8C] hover:text-[#1A1A1A]"
        >
          <GripVertical className="w-5 h-5" />
        </div>
        <button onClick={onToggle} className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-lg text-[#1A1A1A]">{day.day_title || `Day ${day.day_number}`}</h3>
            {isLocked && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#C5A059]/10 text-[#C5A059]">Tomorrow&apos;s Chapter</span>
            )}
          </div>
          <p className="text-sm text-[#8C8C8C]">{dayLabel}</p>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#8C8C8C]">{dayActivities.length} activities</span>
          {expanded ? <ChevronUp className="w-5 h-5 text-[#8C8C8C]" /> : <ChevronDown className="w-5 h-5 text-[#8C8C8C]" />}
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 pb-3">
        {/* Lock/Unlock Toggle */}
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleDayLock && onToggleDayLock(day); }}
          className={`p-1.5 rounded hover:bg-gray-100 ${isLocked ? 'text-[#C5A059]' : 'text-[#8C8C8C]'}`}
          title={isLocked ? 'Unlock day (reveal to client)' : 'Lock day (hide from client as Tomorrow&apos;s Chapter)'}
        >
          {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
        </button>
        
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleDayVisibility(day); }}
          className="p-1.5 rounded hover:bg-gray-100"
          title={isVisible ? 'Hide day from client' : 'Show day to client'}
        >
          {isVisible ? <Eye className="w-4 h-4 text-[#8C8C8C]" /> : <EyeOff className="w-4 h-4 text-[#B37E6B]" />}
        </button>
        
        <button onClick={onDelete} className="p-1.5 rounded hover:bg-gray-100 text-[#B37E6B]">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Teaser Editor - only when locked */}
      {isLocked && (
        <div className="px-4 pb-4 border-t border-[rgba(0,0,0,0.06)] pt-3 bg-[#FAFAFA]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#8C8C8C] uppercase tracking-wide">Teaser Content</span>
            {!editingTeaser ? (
              <button onClick={() => setEditingTeaser(true)} className="text-xs text-[#C5A059] hover:underline">
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveTeaser} className="text-xs text-[#8DA399] hover:underline">Save</button>
                <button onClick={() => setEditingTeaser(false)} className="text-xs text-[#8C8C8C] hover:underline">Cancel</button>
              </div>
            )}
          </div>
          
          {editingTeaser ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Atmospheric Hint</label>
                <Input
                  value={teaserText}
                  onChange={(e) => setTeaserText(e.target.value)}
                  placeholder="Something special awaits..."
                  className="text-sm bg-white"
                />
              </div>
              <div>
                <label className="text-xs text-[#8C8C8C] block mb-1">Riddle (optional)</label>
                <Input
                  value={teaserRiddle}
                  onChange={(e) => setTeaserRiddle(e.target.value)}
                  placeholder="A hint without spoilers..."
                  className="text-sm bg-white"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {day.teaser_text ? (
                <p className="text-sm text-[#595959] italic">&ldquo;{day.teaser_text}&rdquo;</p>
              ) : (
                <p className="text-sm text-[#B0B0B0]">No teaser set</p>
              )}
              {day.teaser_riddle && (
                <p className="text-xs text-[#8C8C8C]">Riddle: {day.teaser_riddle}</p>
              )}
            </div>
          )}
        </div>
      )}
      
      {expanded && (
        <div className="p-4 pt-0 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {['morning', 'afternoon', 'evening'].map((section) => (
            <DaySection
              key={section}
              dayId={day.id}
              section={section}
              activities={dayActivities}
              onAddActivity={onAddActivity}
              onEdit={onEditActivity}
              onDelete={onDeleteActivity}
              onDuplicate={onDuplicateActivity}
              onToggleVisibility={onToggleActivityVisibility}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// Main Itinerary Editor Component
export function ItineraryEditor({ tripId, startDate, endDate, onGenerateClick, generating }) {
  const [days, setDays] = useState([]);
  const [activities, setActivities] = useState([]);
  const [expandedDays, setExpandedDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load data
  React.useEffect(() => {
    if (!tripId) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        const daysRes = await axios.get(`${API}/travels/itinerary/${tripId}`);
        const sortedDays = (daysRes.data || []).sort((a, b) => a.day_number - b.day_number);
        setDays(sortedDays);
        
        // Fetch activities for each day
        const allActivities = [];
        for (const day of sortedDays) {
          const actRes = await axios.get(`${API}/travels/activities/${day.id}`);
          allActivities.push(...(actRes.data || []));
        }
        setActivities(allActivities);
        
        // Auto-expand first day
        if (sortedDays.length > 0) {
          setExpandedDays({ [sortedDays[0].id]: true });
        }
      } catch (err) {
        toast.error('Failed to load itinerary');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [tripId]);

  // Add a new day
  const addDay = async () => {
    try {
      const res = await axios.post(`${API}/travels/itinerary/${tripId}/add-day`);
      setDays([...days, res.data]);
      setExpandedDays({ ...expandedDays, [res.data.id]: true });
      toast.success('Day added');
    } catch (err) {
      toast.error('Failed to add day');
    }
  };

  // Delete a day
  const deleteDay = async (dayId) => {
    if (!window.confirm('Delete this day and all its activities?')) return;
    try {
      await axios.delete(`${API}/travels/itinerary/${dayId}`);
      setDays(days.filter(d => d.id !== dayId));
      setActivities(activities.filter(a => a.day_id !== dayId));
      toast.success('Day deleted');
    } catch (err) {
      toast.error('Failed to delete day');
    }
  };

  // Add activity
  const addActivity = async (dayId, section) => {
    try {
      const res = await axios.post(`${API}/travels/activities`, {
        day_id: dayId,
        section,
        name: 'New Activity',
        order: activities.filter(a => a.day_id === dayId && a.section === section).length
      });
      setActivities([...activities, res.data]);
      toast.success('Activity added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add activity');
    }
  };

  // Edit activity (local update after save)
  const editActivity = (activityId, updates) => {
    setActivities(activities.map(a => a.id === activityId ? { ...a, ...updates } : a));
  };

  // Delete activity
  const deleteActivity = async (activityId) => {
    try {
      await axios.delete(`${API}/travels/activities/${activityId}`);
      setActivities(activities.filter(a => a.id !== activityId));
      toast.success('Activity deleted');
    } catch (err) {
      toast.error('Failed to delete activity');
    }
  };

  // Duplicate activity
  const duplicateActivity = async (activity) => {
    try {
      const res = await axios.post(`${API}/travels/activities/${activity.id}/duplicate`);
      setActivities([...activities, res.data]);
      toast.success('Activity duplicated');
    } catch (err) {
      toast.error('Failed to duplicate activity');
    }
  };

  // Toggle activity visibility
  const toggleActivityVisibility = async (activity) => {
    const newVisibility = !activity.visible_to_client;
    try {
      await axios.patch(`${API}/travels/activities/${activity.id}`, { visible_to_client: newVisibility });
      setActivities(activities.map(a => a.id === activity.id ? { ...a, visible_to_client: newVisibility } : a));
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  // Toggle day visibility
  const toggleDayVisibility = async (day) => {
    const newVisibility = day.visible_to_client === false ? true : false;
    try {
      await axios.patch(`${API}/travels/itinerary/${day.id}`, { visible_to_client: newVisibility });
      setDays(days.map(d => d.id === day.id ? { ...d, visible_to_client: newVisibility } : d));
      toast.success(newVisibility ? 'Day visible to client' : 'Day hidden from client');
    } catch (err) {
      toast.error('Failed to update day visibility');
    }
  };

  // Toggle day lock (Reveal Mode)
  const toggleDayLock = async (day) => {
    try {
      const res = await axios.post(`${API}/travels/itinerary/${day.id}/toggle-lock`);
      setDays(days.map(d => d.id === day.id ? { ...d, locked: res.data.locked } : d));
      toast.success(res.data.locked ? 'Day locked - shows as "Tomorrow\'s Chapter" to clients' : 'Day unlocked - visible to clients');
    } catch (err) {
      toast.error('Failed to toggle day lock');
    }
  };

  // Update day teaser
  const updateTeaser = async (dayId, teaserText, teaserRiddle) => {
    try {
      await axios.patch(`${API}/travels/itinerary/${dayId}`, { 
        teaser_text: teaserText, 
        teaser_riddle: teaserRiddle 
      });
      setDays(days.map(d => d.id === dayId ? { ...d, teaser_text: teaserText, teaser_riddle: teaserRiddle } : d));
    } catch (err) {
      throw err;
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveItem(active.data.current);
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);

    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Reordering days
    if (activeData?.type === 'day' && overData?.type === 'day') {
      const oldIndex = days.findIndex(d => d.id === active.id);
      const newIndex = days.findIndex(d => d.id === over.id);
      
      const newDays = arrayMove(days, oldIndex, newIndex).map((d, i) => ({ ...d, day_number: i + 1 }));
      setDays(newDays);
      
      // Persist to backend
      try {
        await axios.post(`${API}/travels/itinerary/reorder?trip_id=${tripId}`, newDays.map(d => d.id));
      } catch (err) {
        toast.error('Failed to reorder days');
      }
      return;
    }

    // Reordering activities
    if (activeData?.type === 'activity') {
      const activeActivity = activeData.activity;
      
      // Find target day and section
      let targetDayId = activeActivity.day_id;
      let targetSection = activeActivity.section;
      
      if (overData?.type === 'activity') {
        const overActivity = overData.activity;
        targetDayId = overActivity.day_id;
        targetSection = overActivity.section;
      }
      
      // Get activities in target section
      const sectionActivities = activities
        .filter(a => a.day_id === targetDayId && a.section === targetSection)
        .sort((a, b) => a.order - b.order);
      
      const oldIndex = sectionActivities.findIndex(a => a.id === active.id);
      const newIndex = sectionActivities.findIndex(a => a.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Same section reorder
        const newOrder = arrayMove(sectionActivities, oldIndex, newIndex);
        const updatedActivities = activities.map(a => {
          const idx = newOrder.findIndex(na => na.id === a.id);
          if (idx !== -1) return { ...a, order: idx };
          return a;
        });
        setActivities(updatedActivities);
        
        // Persist
        try {
          await axios.post(`${API}/travels/activities/reorder?day_id=${targetDayId}&section=${targetSection}`, 
            newOrder.map(a => a.id)
          );
        } catch (err) {
          toast.error('Failed to reorder activities');
        }
      } else if (targetDayId !== activeActivity.day_id || targetSection !== activeActivity.section) {
        // Move to different day/section
        const targetOrder = sectionActivities.length;
        
        const updatedActivities = activities.map(a => 
          a.id === activeActivity.id 
            ? { ...a, day_id: targetDayId, section: targetSection, order: targetOrder }
            : a
        );
        setActivities(updatedActivities);
        
        // Persist
        try {
          await axios.post(`${API}/travels/activities/move?activity_id=${activeActivity.id}&target_day_id=${targetDayId}&target_section=${targetSection}&target_order=${targetOrder}`);
        } catch (err) {
          toast.error('Failed to move activity');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  if (!tripId) {
    return (
      <div className="bg-[#C5A059]/10 rounded-xl p-4">
        <p className="text-sm text-[#C5A059]">Save the trip first to add itinerary days</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-xl text-[#1A1A1A]">Daily Itinerary</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={onGenerateClick} 
            disabled={generating} 
            className="border-[#C5A059] text-[#C5A059]"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {generating ? 'Generating...' : 'Generate Draft'}
          </Button>
          <Button onClick={addDay} className="bg-[#2C2C2C] text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Day
          </Button>
        </div>
      </div>

      {days.length === 0 ? (
        <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-12 text-center">
          <Sparkles className="w-12 h-12 text-[#E6E4DE] mx-auto mb-3" />
          <p className="text-[#8C8C8C] mb-2">No itinerary days yet</p>
          <p className="text-xs text-[#B0B0B0]">Click &ldquo;Generate Draft&rdquo; for AI suggestions or &ldquo;Add Day&rdquo; to build manually</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={days.map(d => d.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {days.map((day) => (
                <SortableDayCard
                  key={day.id}
                  day={day}
                  startDate={startDate}
                  endDate={endDate}
                  activities={activities}
                  expanded={expandedDays[day.id]}
                  onToggle={() => setExpandedDays({ ...expandedDays, [day.id]: !expandedDays[day.id] })}
                  onDelete={() => deleteDay(day.id)}
                  onAddActivity={addActivity}
                  onEditActivity={editActivity}
                  onDeleteActivity={deleteActivity}
                  onDuplicateActivity={duplicateActivity}
                  onToggleActivityVisibility={toggleActivityVisibility}
                  onToggleDayVisibility={toggleDayVisibility}
                  onToggleDayLock={toggleDayLock}
                  onUpdateTeaser={updateTeaser}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeId && activeItem?.type === 'activity' && (
              <ActivityCardOverlay activity={activeItem.activity} />
            )}
            {activeId && activeItem?.type === 'day' && (
              <div className="bg-white rounded-xl border-2 border-[#C5A059] shadow-lg p-4 w-96">
                <h3 className="font-serif text-lg text-[#1A1A1A]">{activeItem.day.day_title || `Day ${activeItem.day.day_number}`}</h3>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
